import { switchAccountRequest } from '@ionq/auth-api';
import { toaster } from '@ionq/legacy-ui';
import { createSessionContext, useSessionExpiry } from '@ionq/react-session';
import type { UsersMeResponse } from '@ionq/trpc-console';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { remoteSession } from '../lib/remoteSession';
import { getSwitchLandingURL } from '../lib/session';
import { latchSessionRevalidation } from '../lib/sessionRevalidation';
import { queryClient, setOnSessionExpired } from '../lib/trpc';

// Real session over the console-backend users.me payload; the header
// adapters and sidebar consume this surface only (no remote-root reads).

type SessionUser = {
    firstName: string | null;
    lastName: string | null;
    email: string;
};

type SessionOrganization = UsersMeResponse['organizations'][number];

const { Provider, useSession: useSessionBase } = createSessionContext<{
    user: SessionUser;
    organizations: SessionOrganization[];
    currentOrganization: SessionOrganization;
    /** Switches to another org's account: POST /auth/api/switch-account
     * re-mints the session cookie server-side, then both sides' caches
     * refetch under it — in place when the remote exposes ./session, via a
     * full document load otherwise. */
    switchOrganization: (id: string) => void;
    /** The session deadline passed, or a tRPC call answered UNAUTHORIZED. */
    isExpired: boolean;
}>();

export const useSession = useSessionBase;

export function SessionProvider({
    user,
    children,
}: {
    user: UsersMeResponse;
    children: ReactNode;
}) {
    // Ref, not state: the guard must flip synchronously on click.
    const inFlight = useRef(false);
    const navigate = useNavigate();

    // Same two expiry signals as enterprise: tRPC auth errors (lib/trpc's
    // cache callbacks) OR'd with the shared deadline timer.
    const [authExpired, setAuthExpired] = useState(false);
    useEffect(() => {
        setOnSessionExpired(() => setAuthExpired(true));
        return () => setOnSessionExpired(null);
    }, []);
    const timerExpired = useSessionExpiry(user.session.expiresAt);
    const isExpired = timerExpired || authExpired;

    const value = useMemo(() => {
        const { organizations } = user;
        const currentOrganization = organizations.find(
            (org) => org.id === user.organization,
        ) ?? {
            // Session org missing from the list (e.g. deleted under the
            // session): the switcher hero renders the id fallback.
            id: user.organization,
            name: null,
            logoUrl: null,
            partnerId: null,
            accountId: user.accountId,
            userRole: user.role,
        };

        const switchOrganization = (orgId: string) => {
            if (inFlight.current || orgId === user.organization) return;
            // Rows are keyed by org id; the switch call needs that org's
            // accountId.
            const target = organizations.find((org) => org.id === orgId);
            if (!target?.accountId) return;
            const { accountId } = target;
            inFlight.current = true;
            void (async () => {
                // The remote's progress overlay covers the POST and the
                // cache churn (no-op without the ./session expose).
                remoteSession?.setAccountSwitchTarget(
                    target.name ?? 'your organization',
                );
                try {
                    await switchAccountRequest(accountId);
                    const landing = getSwitchLandingURL({
                        from: window.location.href,
                    });
                    if (remoteSession) {
                        // No-reload switch: reset the remote's cache (its
                        // loaders refetch under the re-minted cookie — the
                        // shell can't reach that module-singleton client any
                        // other way), drop the shell's cached identity, and
                        // latch the root loader to re-run. Same-URL landings
                        // still revalidate: the root loader is latched and
                        // remote-root always revalidates.
                        // Not awaited (cloud-ui's own flow voids it too):
                        // the cache reset is synchronous — the promise only
                        // tracks the outgoing page's refetches, which unmount
                        // on navigation.
                        remoteSession
                            .resetForAccountSwitch()
                            .catch(() => undefined);
                        queryClient.clear();
                        latchSessionRevalidation();
                        await navigate(landing, { replace: true });
                        inFlight.current = false;
                        return;
                    }
                    // Fallback (older remote builds): a full document load is
                    // the only way to rebuild the remote's cache. replace():
                    // the old org's URL is dead and must not survive in
                    // history; same-URL landings would resolve as a fragment
                    // navigation, so reload explicitly.
                    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                    if (landing === current) window.location.reload();
                    else window.location.replace(landing);
                } catch {
                    inFlight.current = false;
                    toaster.error({
                        title: 'Could not switch organization. Please try again.',
                    });
                } finally {
                    // After the navigation settles (loaders done, chrome
                    // fresh) — or on failure/unload, where it's a no-op.
                    remoteSession?.setAccountSwitchTarget(null);
                }
            })();
        };

        return {
            user: {
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
            },
            organizations,
            currentOrganization,
            switchOrganization,
            isExpired,
        };
    }, [user, navigate, isExpired]);

    return <Provider value={value}>{children}</Provider>;
}
